import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, media } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { FlaskConical, Search } from "lucide-react";

export default function Catalog() {
  const [peptides, setPeptides] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [cat, setCat] = useState("");
  const [sort, setSort] = useState("featured");
  const [params] = useSearchParams();

  useEffect(() => {
    setCat(params.get("category") || "");
    api.get("/peptides").then(({ data }) => setPeptides(data)).catch(() => {});
  }, [params]);

  const cats = [...new Set(peptides.flatMap((p) => p.categories || []))];

  let filtered = peptides.filter((p) => {
    const ql = q.toLowerCase().replace(/[^a-z0-9]/g, "");
    const matchesQ = !ql || p.name.toLowerCase().replace(/[^a-z0-9]/g, "").includes(ql) || (p.aliases || []).some((a) => a.toLowerCase().replace(/[^a-z0-9]/g, "").includes(ql));
    const matchesT = !type || p.peptide_type === type;
    const matchesC = !cat || (p.categories || []).some((c) => c.toLowerCase() === cat.toLowerCase());
    return matchesQ && matchesT && matchesC;
  });
  if (sort === "alpha") filtered = filtered.slice().sort((a, b) => a.name.localeCompare(b.name));
  if (sort === "mw") filtered = filtered.slice().sort((a, b) => (a.molecular_weight || 0) - (b.molecular_weight || 0));

  return (
    <div className="px-4 sm:px-8 lg:px-12 pt-28 pb-20 max-w-7xl mx-auto">
      <div className="mono-label mb-3">Peptide / Science / Longevity</div>
      <h1 className="font-serif text-5xl sm:text-6xl text-white mb-8">The Peptide Library</h1>

      <div className="flex flex-wrap gap-3 mb-8">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search BPC, BPC157, BPC-157…" data-testid="catalog-search-input"
            className="pl-9 bg-black/40 border-white/10" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="catalog-type-filter"
          className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-300">
          <option value="">All types</option>
          <option value="single">Single</option>
          <option value="blend">Blend</option>
        </select>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="catalog-category-filter"
          className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-300">
          <option value="">All categories</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="catalog-sort"
          className="bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-slate-300">
          <option value="featured">Featured</option>
          <option value="alpha">A → Z</option>
          <option value="mw">Molecular weight</option>
        </select>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="peptide-catalog-grid">
        {filtered.map((p) => (
          <Link key={p.id} to={`/peptides/${p.slug}`} data-testid={`peptide-card-${p.slug}`}
            className="glass rounded-3xl p-6 group hover:border-cyan-400/40 hover:glow-cyan transition-all duration-300">
            {p.image && (
              <div className="-mx-6 -mt-6 mb-5 aspect-square rounded-t-3xl overflow-hidden bg-black/40" data-testid={`peptide-card-image-${p.slug}`}>
                <img src={media(p.image)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            )}
            <div className="flex items-start justify-between mb-4">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-400/10 to-violet-500/10 border border-white/10 flex items-center justify-center group-hover:border-cyan-400/40 transition-colors">
                <FlaskConical className="text-cyan-300" size={18} />
              </div>
              <span className="stat-pill">{p.peptide_type === "blend" ? "Blend" : "Single"}</span>
            </div>
            <div className="font-serif text-2xl text-white">{p.name}</div>
            <div className="text-xs text-slate-500 font-mono mt-1 truncate">{p.scientific_name}</div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {(p.categories || []).slice(0, 2).map((c) => (
                <span key={c} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-mono text-slate-400">{c}</span>
              ))}
            </div>
            {p.molecular_weight > 0 && <div className="mt-3 text-[11px] font-mono text-cyan-400/70">MW {p.molecular_weight} Da</div>}
          </Link>
        ))}
      </div>
      {filtered.length === 0 && <div className="text-center py-20 text-slate-500">No peptides matched. Try a different search.</div>}
    </div>
  );
}
